/**
 * Organisation admin — guided onboarding workflow.
 *
 * The three parallel forms become a stepper with visible dependencies and a
 * review step, so a multi-site rollout reads as a process, not a wall of
 * inputs. The invariant stays on screen: sites, machines and camera stations
 * are rows, not deployments — no new backend code per customer.
 */
import { useState } from 'react';
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import type { FleetOverview, Recipe } from '../api/types';
import { Card, Empty, ErrorBox, Loading, StatePill } from '../components/Metric';

const INDUSTRIES = ['AUTOMOTIVE', 'MEDICAL_DEVICE', 'FOOD_PACKAGING', 'CONSTRUCTION', 'COMPOUNDING_RECYCLING', 'GENERAL'];
const QMS = ['NONE', 'ISO_9001', 'IATF_16949', 'ISO_13485', 'OTHER'];
const PROCESS_TYPES = ['INJECTION_MOLDING', 'EXTRUSION', 'BLOW_MOLDING', 'THERMOFORMING', 'COMPOUNDING', 'RECYCLING'];
const SENSORS = ['MONO_AREA_SCAN', 'RGB_AREA_SCAN', 'LINE_SCAN', 'NEAR_INFRARED', 'HYPERSPECTRAL', 'LASER_TRIANGULATION_3D', 'TERAHERTZ', 'ACOUSTIC_EMISSION'];
const LIGHTING = ['BACKLIGHT', 'DARK_FIELD', 'POLARIZED', 'NEAR_INFRARED', 'GRAZING', 'TRANSMISSION', 'AMBIENT_DOME', 'DIRECTIONAL'];

type StepId = 'site' | 'machine' | 'station' | 'license' | 'review';

export function AdminPage() {
  const fleet = useApi<FleetOverview>(() => api.get<FleetOverview>('/api/v1/fleet/overview'), []);
  const recipes = useApi<Recipe[]>(() => api.get<Recipe[]>('/api/v1/recipes'), []);

  const [step, setStep] = useState<StepId>('site');
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [done, setDone] = useState<Record<StepId, boolean>>({
    site: false,
    machine: false,
    station: false,
    license: false,
    review: false,
  });

  const [site, setSite] = useState({
    name: '',
    country: 'CH',
    region: 'EU',
    industry: 'GENERAL',
    qmsFramework: 'NONE',
    escapeRateTolerancePct: '0.5',
  });
  const [machine, setMachine] = useState({
    siteId: '',
    name: '',
    vendor: '',
    processType: 'INJECTION_MOLDING',
    cycleTimeSeconds: '30',
  });
  const [station, setStation] = useState({
    machineId: '',
    name: '',
    sensorType: 'MONO_AREA_SCAN',
    lightingMode: 'BACKLIGHT',
    lightingNotes: '',
    recipeId: '',
  });
  const [created, setCreated] = useState<{ site?: string; machine?: string; station?: string }>({});

  async function run<T>(fn: () => Promise<T>, message: string, mark?: StepId) {
    setBusy(true);
    setFail(null);
    setOk(null);
    try {
      const r = await fn();
      setOk(message);
      if (mark) setDone((d) => ({ ...d, [mark]: true }));
      fleet.reload();
      return r;
    } catch (err) {
      setFail((err as Error).message);
      return null;
    } finally {
      setBusy(false);
    }
  }

  const createSite = () =>
    run(async () => {
      const s = await api.post<{ id: string; name: string }>('/api/v1/sites', {
        ...site,
        escapeRateTolerancePct: Number(site.escapeRateTolerancePct),
      });
      setMachine((m) => ({ ...m, siteId: s.id }));
      setCreated((c) => ({ ...c, site: s.name }));
      setStep('machine');
    }, 'Site registered.', 'site');

  const createMachine = () =>
    run(async () => {
      const m = await api.post<{ id: string; name: string }>('/api/v1/machines', {
        ...machine,
        cycleTimeSeconds: Number(machine.cycleTimeSeconds) || undefined,
        vendor: machine.vendor || undefined,
      });
      setStation((s) => ({ ...s, machineId: m.id }));
      setCreated((c) => ({ ...c, machine: m.name }));
      setStep('station');
    }, 'Machine onboarded.', 'machine');

  const createStation = () =>
    run(async () => {
      const s = await api.post<{ id: string }>('/api/v1/stations', {
        machineId: station.machineId,
        name: station.name,
        sensorType: station.sensorType,
        lightingMode: station.lightingMode,
        lightingNotes: station.lightingNotes || undefined,
      });
      if (station.recipeId) {
        await api.patch(`/api/v1/stations/${s.id}`, { activeRecipeId: station.recipeId });
      }
      setCreated((c) => ({ ...c, station: station.name }));
      setStep('license');
    }, 'Camera station onboarded.', 'station');

  const licenseMachine = (id: string, name: string) =>
    run(
      () => api.post('/api/v1/machines/license', { machineId: id }),
      `Licence recorded for ${name}.`,
      'license',
    );

  const steps: Array<{ id: StepId; t: string; d: string }> = [
    { id: 'site', t: 'Register site', d: 'Legal & QMS context' },
    { id: 'machine', t: 'Add machine', d: 'Consumes a licence slot' },
    { id: 'station', t: 'Add camera station', d: 'Sensor, lighting, recipe' },
    { id: 'license', t: 'Record licence', d: 'Per machine, never per camera' },
    { id: 'review', t: 'Review & ready', d: 'Hand over to the operator' },
  ];

  if (fleet.loading) return <Loading label="Loading inventory…" />;
  if (fleet.error) return <ErrorBox message={fleet.error} />;

  return (
    <>
      <div className="callout info">
        <strong>No new backend code per customer</strong>
        A new site, machine and camera station are rows, not a deployment. That
        is the property that makes this a marketplace module rather than a
        bespoke integration for every plant.
      </div>

      {fail && <ErrorBox message={fail} />}
      {ok && <div className="callout info">{ok}</div>}

      <div className="wizard">
        <div className="wizard-rail" role="tablist" aria-label="Onboarding steps">
          {steps.map((s, i) => (
            <button
              key={s.id}
              role="tab"
              aria-selected={step === s.id}
              className={`wizard-step ${step === s.id ? 'on' : ''} ${done[s.id] ? 'done' : ''}`}
              onClick={() => setStep(s.id)}
            >
              <span className="n">{done[s.id] ? '✓' : i + 1}</span>
              <span>
                <span className="t" style={{ display: 'block' }}>{s.t}</span>
                <span className="d">{s.d}</span>
              </span>
            </button>
          ))}
        </div>

        <div>
          {step === 'site' && (
            <Card title="1 · Register site" className="rise">
              <div className="field">
                <label htmlFor="site-name">Site name</label>
                <input id="site-name" value={site.name} onChange={(e) => setSite({ ...site, name: e.target.value })} placeholder="Winterthur Plant 2" />
              </div>
              <div className="grid grid-2">
                <div className="field">
                  <label htmlFor="site-country">Country (ISO-2)</label>
                  <input id="site-country" maxLength={2} value={site.country} onChange={(e) => setSite({ ...site, country: e.target.value.toUpperCase() })} />
                </div>
                <div className="field">
                  <label htmlFor="site-region">Region</label>
                  <select id="site-region" value={site.region} onChange={(e) => setSite({ ...site, region: e.target.value })}>
                    {['EU', 'GCC', 'NORTH_AMERICA', 'OTHER'].map((r) => (
                      <option key={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-2">
                <div className="field">
                  <label htmlFor="site-industry">Industry</label>
                  <select id="site-industry" value={site.industry} onChange={(e) => setSite({ ...site, industry: e.target.value })}>
                    {INDUSTRIES.map((i) => (
                      <option key={i}>{i}</option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="site-qms">QMS framework</label>
                  <select id="site-qms" value={site.qmsFramework} onChange={(e) => setSite({ ...site, qmsFramework: e.target.value })}>
                    {QMS.map((q) => (
                      <option key={q}>{q}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field" style={{ maxWidth: 220 }}>
                <label htmlFor="site-escape">Escape tolerance %</label>
                <input
                  id="site-escape"
                  inputMode="decimal"
                  value={site.escapeRateTolerancePct}
                  onChange={(e) => setSite({ ...site, escapeRateTolerancePct: e.target.value })}
                />
                <div className="hint">The site&rsquo;s own contractual escape-rate limit.</div>
              </div>
              <button className="btn primary" disabled={busy || !site.name} onClick={() => void createSite()}>
                Register site
              </button>
            </Card>
          )}

          {step === 'machine' && (
            <Card title="2 · Add machine" className="rise">
              <div className="field">
                <label htmlFor="machine-site">Site</label>
                <select id="machine-site" value={machine.siteId} onChange={(e) => setMachine({ ...machine, siteId: e.target.value })}>
                  <option value="">Select…</option>
                  {fleet.data?.sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="machine-name">Machine name</label>
                <input id="machine-name" value={machine.name} onChange={(e) => setMachine({ ...machine, name: e.target.value })} placeholder="IMM 04 — Engel 350" />
              </div>
              <div className="grid grid-2">
                <div className="field">
                  <label htmlFor="machine-vendor">Vendor</label>
                  <input id="machine-vendor" value={machine.vendor} onChange={(e) => setMachine({ ...machine, vendor: e.target.value })} placeholder="Engel" />
                </div>
                <div className="field">
                  <label htmlFor="machine-cycle">Cycle time (s)</label>
                  <input id="machine-cycle" inputMode="decimal" value={machine.cycleTimeSeconds} onChange={(e) => setMachine({ ...machine, cycleTimeSeconds: e.target.value })} />
                </div>
              </div>
              <div className="field">
                <label htmlFor="machine-process">Process type</label>
                <select id="machine-process" value={machine.processType} onChange={(e) => setMachine({ ...machine, processType: e.target.value })}>
                  {PROCESS_TYPES.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </div>
              <button
                className="btn primary"
                disabled={busy || !machine.siteId || !machine.name}
                onClick={() => void createMachine()}
              >
                Onboard machine
              </button>
              <div className="card-note">
                Billing is per machine. Registering the machine is what consumes a
                licence slot — adding cameras to it does not.
              </div>
            </Card>
          )}

          {step === 'station' && (
            <Card title="3 · Add camera station" className="rise">
              <div className="field">
                <label htmlFor="station-machine">Machine</label>
                <select id="station-machine" value={station.machineId} onChange={(e) => setStation({ ...station, machineId: e.target.value })}>
                  <option value="">Select…</option>
                  {fleet.data?.sites.flatMap((s) =>
                    s.machines.map((m) => (
                      <option key={m.id} value={m.id}>
                        {s.name} · {m.name}
                      </option>
                    )),
                  )}
                </select>
              </div>
              <div className="field">
                <label htmlFor="station-name">Station name</label>
                <input id="station-name" value={station.name} onChange={(e) => setStation({ ...station, name: e.target.value })} placeholder="Cavity camera A" />
              </div>
              <div className="grid grid-2">
                <div className="field">
                  <label htmlFor="station-sensor">Sensor</label>
                  <select id="station-sensor" value={station.sensorType} onChange={(e) => setStation({ ...station, sensorType: e.target.value })}>
                    {SENSORS.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="station-lighting">Lighting</label>
                  <select id="station-lighting" value={station.lightingMode} onChange={(e) => setStation({ ...station, lightingMode: e.target.value })}>
                    {LIGHTING.map((l) => (
                      <option key={l}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field">
                <label htmlFor="station-lighting-notes">Lighting notes</label>
                <input
                  id="station-lighting-notes"
                  value={station.lightingNotes}
                  onChange={(e) => setStation({ ...station, lightingNotes: e.target.value })}
                  placeholder="Backlit for silhouette flash detection"
                />
                <div className="hint">Recorded so the setup can be reproduced after maintenance.</div>
              </div>
              <div className="field">
                <label htmlFor="station-recipe">Inspection recipe</label>
                <select id="station-recipe" value={station.recipeId} onChange={(e) => setStation({ ...station, recipeId: e.target.value })}>
                  <option value="">Bind later</option>
                  {recipes.data?.map((r) => (
                    <option key={r.id} value={r.id} disabled={!r.available}>
                      {r.name}
                      {r.available ? '' : ' (coming soon)'}
                    </option>
                  ))}
                </select>
                <div className="hint">
                  Recipes outside this deployment&rsquo;s rollout phase are
                  refused server-side with the reason stated.
                </div>
              </div>
              <button
                className="btn primary"
                disabled={busy || !station.machineId || !station.name}
                onClick={() => void createStation()}
              >
                Onboard station
              </button>
              <div className="card-note">
                Stations start in PASSIVE. The operator learns a baseline before
                inspection begins.
              </div>
            </Card>
          )}

          {step === 'license' && (
            <Card title="4 · Record licence" className="rise">
              <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
                The marketplace subscription is per machine. Record which machines
                are covered; camera stations on a licensed machine are included.
              </p>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Machine</th>
                      <th>Licensed</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {fleet.data?.sites.flatMap((s) =>
                      s.machines.map((m) => (
                        <tr key={m.id}>
                          <td style={{ fontWeight: 600 }}>
                            {s.name} · {m.name}
                          </td>
                          <td>
                            {m.licensed ? <span className="pill ok">yes</span> : <span className="pill muted">no</span>}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {!m.licensed && (
                              <button className="btn sm" disabled={busy} onClick={() => void licenseMachine(m.id, m.name)}>
                                Record licence
                              </button>
                            )}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
              <div className="btn-row" style={{ marginTop: 12 }}>
                <button className="btn ghost" onClick={() => setStep('review')}>
                  Continue to review →
                </button>
              </div>
            </Card>
          )}

          {step === 'review' && (
            <Card title="5 · Review & ready" className="rise">
              <table className="kv">
                <tbody>
                  <tr>
                    <td>Site</td>
                    <td>{created.site ?? '—'}</td>
                  </tr>
                  <tr>
                    <td>Machine</td>
                    <td>{created.machine ?? '—'}</td>
                  </tr>
                  <tr>
                    <td>Camera station</td>
                    <td>{created.station ?? '—'} · starts PASSIVE</td>
                  </tr>
                  <tr>
                    <td>Recipe</td>
                    <td>{recipes.data?.find((r) => r.id === station.recipeId)?.name ?? 'Bind later'}</td>
                  </tr>
                </tbody>
              </table>
              <div className="callout info" style={{ marginTop: 14, marginBottom: 0 }}>
                <strong>Hand over to the operator</strong>
                The operator now sees the station on “My station”: photograph
                20–50 known-good parts, enter shadow, review the accuracy report
                against the inspector&rsquo;s scrap log, then go active. No
                further admin action — and no new backend code — is required.
              </div>
              <div className="btn-row" style={{ marginTop: 12 }}>
                <button
                  className="btn primary"
                  onClick={() => setDone((d) => ({ ...d, review: true }))}
                >
                  Mark deployment ready
                </button>
              </div>
            </Card>
          )}
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Card title="Inventory">
          {!fleet.data || fleet.data.sites.length === 0 ? (
            <Empty title="Nothing registered yet" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Site</th>
                    <th>Machine</th>
                    <th>Licensed</th>
                    <th>Station</th>
                    <th>State</th>
                    <th className="num">Baseline</th>
                  </tr>
                </thead>
                <tbody>
                  {fleet.data.sites.flatMap((s) =>
                    s.machines.flatMap((m) =>
                      m.cameraStations.length === 0
                        ? [
                            <tr key={m.id}>
                              <td>{s.name}</td>
                              <td>{m.name}</td>
                              <td>{m.licensed ? <span className="pill ok">yes</span> : <span className="pill muted">no</span>}</td>
                              <td colSpan={3} style={{ color: 'var(--ink-3)' }}>
                                No station
                              </td>
                            </tr>,
                          ]
                        : m.cameraStations.map((st) => (
                            <tr key={st.id}>
                              <td>{s.name}</td>
                              <td>{m.name}</td>
                              <td>{m.licensed ? <span className="pill ok">yes</span> : <span className="pill muted">no</span>}</td>
                              <td style={{ fontWeight: 600 }}>{st.name}</td>
                              <td>
                                <StatePill state={st.state} />
                              </td>
                              <td className="num">
                                {st.baselineImageCount}
                                {st.baselineReady ? '' : ' (incomplete)'}
                              </td>
                            </tr>
                          )),
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
